import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import chesiLogo from '../assets/chesi-logo.png';
import { useAuth } from '../context/AuthContext';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ReplyAllOutlinedIcon from '@mui/icons-material/ReplyAllOutlined';
import './MainMenu.css';

const BASE_SIDEBAR_ITEMS = [
  { Icon: HomeOutlinedIcon, name: 'Home', route: '/' },
  { Icon: UploadFileOutlinedIcon, name: 'STO Import', route: '/sto-import' },
  { Icon: ListAltOutlinedIcon, name: 'STO List', route: '/sto-list' },
  { Icon: ReplyAllOutlinedIcon, name: 'Recall List', route: '/recall-list' },
  { Icon: Inventory2OutlinedIcon, name: 'Inventory', route: '/inventory' },
  { Icon: LocalShippingOutlinedIcon, name: 'Dispatch', route: '/delivery' },
  { Icon: AssessmentOutlinedIcon, name: 'Reports', route: '/reports' },
];

const PICK_LIST_SIDEBAR_ITEM = { Icon: AssignmentOutlinedIcon, name: 'Pick List', route: '/pick-list' };

const ADMIN_SIDEBAR_ITEMS = [
  { Icon: LocationOnOutlinedIcon, name: 'Locations', route: '/admin/locations' },
  { Icon: PeopleOutlinedIcon, name: 'Users', route: '/admin/users' },
  { Icon: GroupsOutlinedIcon, name: 'Customers', route: '/admin/customers' },
];

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isMainStore, isPicker, fetchWithAuth } = useAuth();
  const displayName = user?.displayName || user?.email || 'User';
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [headerLogoUrl, setHeaderLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const logoUrlRef = useRef(null);

  const loadHeaderLogo = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/settings/logo');
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
        logoUrlRef.current = url;
        setHeaderLogoUrl(url);
      } else {
        if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
        logoUrlRef.current = null;
        setHeaderLogoUrl(null);
      }
    } catch {
      if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
      logoUrlRef.current = null;
      setHeaderLogoUrl(null);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadHeaderLogo();
    return () => {
      if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current);
    };
  }, [loadHeaderLogo]);

  const handleLogoClick = () => {
    if (logoUploading) return;
    logoInputRef.current?.click();
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    e.target.value = '';
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetchWithAuth('/api/settings/logo', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        await loadHeaderLogo();
      }
    } finally {
      setLogoUploading(false);
    }
  };

  const sidebarItems = useMemo(() => {
    if (isPicker) {
      return [{ Icon: HomeOutlinedIcon, name: 'Home', route: '/' }, PICK_LIST_SIDEBAR_ITEM];
    }
    const base = [...BASE_SIDEBAR_ITEMS];
    if (isMainStore) {
      base.push(PICK_LIST_SIDEBAR_ITEM);
    }
    if (isAdmin) base.push(...ADMIN_SIDEBAR_ITEMS);
    return base;
  }, [isAdmin, isMainStore, isPicker]);

  const isSidebarActive = useCallback((pathname, item) => {
    if (!item.route) return false;
    return pathname === item.route || (item.route !== '/' && pathname.startsWith(item.route));
  }, []);

  const handleSidebarItemClick = (item) => {
    if (item.route) {
      navigate(item.route);
    }
  };

  return (
    <div className="main-menu-container">
      {/* Top Header Bar */}
      <div className="top-header">
        <div className="header-left">
          <button className="hamburger-btn" onClick={() => setSidebarOpen((o) => !o)} aria-label={sidebarOpen ? 'Collapse menu' : 'Expand menu'}>
            <span className="hamburger-icon">☰</span>
          </button>
          <div className="chesi-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={chesiLogo} alt="Chesi Logo" className="logo-image" />
          </div>
          <div className="header-logo-section" onClick={handleLogoClick} role="button" tabIndex={0} onKeyDown={(ev) => ev.key === 'Enter' && handleLogoClick()} aria-label={headerLogoUrl ? 'Change logo' : 'Add logo'}>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleLogoFileChange}
              style={{ display: 'none' }}
              aria-hidden
            />
            {headerLogoUrl ? (
              <img src={headerLogoUrl} alt="Company logo" className="header-logo-image" />
            ) : (
              <span className="header-logo-placeholder">{logoUploading ? 'Uploading…' : '+ Add logo'}</span>
            )}
          </div>
        </div>
        
        <div className="header-center">
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input type="text" placeholder="Search here" className="search-input" />
          </div>
        </div>
        
        <div className="header-right">
          <button className="header-btn">EN</button>
          <button className="header-btn notification-btn">
            🔔
            <span className="badge">7</span>
          </button>
          <button className="header-btn mail-btn">
            ✉️
            <span className="badge">12</span>
          </button>
          <button className="header-btn">🔄</button>
          <button className="header-btn">⚙️</button>
          <button className="header-btn">✕</button>
          <Button size="small" onClick={() => logout()} sx={{ mr: 1 }}>
            Logout
          </Button>
          <div className="user-profile">
            <span className="user-name">{displayName}</span>
            <div className="profile-picture">👤</div>
          </div>
        </div>
      </div>

      <div className="main-layout">
        {/* Left Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-content">
            {sidebarItems.map((item, index) => (
              <div
                key={index}
                className={`sidebar-item ${isSidebarActive(location.pathname, item) ? 'active' : ''}`}
                title={item.name}
                onClick={() => handleSidebarItemClick(item)}
                style={{ cursor: item.route ? 'pointer' : 'default' }}
              >
                <item.Icon className="sidebar-icon" />
                <span className="sidebar-item-label">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="main-content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;

